import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { StatsCard } from "@/components/admin/StatsCard";
import { DailyHistoryChart } from "@/components/admin/DailyHistoryChart";
import { RecentVisitsList } from "@/components/admin/RecentVisitsList";
import { CountryBreakdownChart } from "@/components/admin/CountryBreakdownChart";

function formatDuration(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}

export default function AdminOverview() {
  const [stats, setStats] = useState({
    books: 0,
    posts: 0,
    messages: 0,
    unread: 0,
    totalReads: 0,
    totalLikes: 0,
    visits: 0,
    uniqueVisits: 0,
    subscribers: 0,
  });
  const [topPosts, setTopPosts] = useState([]);
  const [readStats, setReadStats] = useState([]);
  const [locations, setLocations] = useState({ countries: [], cities: [] });
  const [history, setHistory] = useState({ from: "", total_visits: 0, total_unique: 0, daily: [] });

  useEffect(() => {
    Promise.all([
      api.get("/books"),
      api.get("/posts", { params: { include_unpublished: true } }),
      api.get("/contact"),
      api.get("/stats"),
      api.get("/stats/admin"),
      api.get("/posts/read-stats"),
      api.get("/stats/admin/history"),
      api.get("/newsletter/subscribers"),
    ])
      .then(([b, p, c, s, sa, rs, hist, subs]) => {
        const posts = p.data || [];
        const totalReads = posts.reduce((sum, x) => sum + (x.views || 0), 0);
        const totalLikes = (rs.data || []).reduce((sum, x) => sum + (x.likes || 0), 0);
        const sortedByReads = [...posts].sort((a, b) => (b.views || 0) - (a.views || 0));
        setStats({
          books: b.data.length,
          posts: posts.length,
          messages: c.data.length,
          unread: c.data.filter((m) => !m.read).length,
          totalReads,
          totalLikes,
          visits: s.data.visits || 0,
          uniqueVisits: sa.data.unique_visits || 0,
          subscribers: subs.data?.active || 0,
        });
        setTopPosts(sortedByReads.slice(0, 3));
        setReadStats(
          (rs.data || [])
            .filter((x) => (x.views || 0) > 0 || (x.read_events || 0) > 0 || (x.likes || 0) > 0)
            .sort((a, b) => (b.total_read_seconds || 0) - (a.total_read_seconds || 0))
            .slice(0, 8)
        );
        setLocations({
          countries: sa.data.top_countries || [],
          cities: sa.data.top_cities || [],
        });
        setHistory(hist.data || { from: "", total_visits: 0, total_unique: 0, daily: [] });
      })
      .catch(() => {});
  }, []);

  const cards = [
    { label: "Books", value: stats.books, testid: "stat-books" },
    { label: "Journal entries", value: stats.posts, testid: "stat-posts" },
    { label: "Letters", value: stats.messages, testid: "stat-messages" },
    { label: "Unread", value: stats.unread, testid: "stat-unread", accent: stats.unread > 0 },
    { label: "Total reads", value: stats.totalReads, testid: "stat-total-reads" },
    { label: "Total likes", value: stats.totalLikes, testid: "stat-total-likes", accent: stats.totalLikes > 0 },
    { label: "Site visits", value: stats.visits, testid: "stat-visits" },
    { label: "Unique visitors", value: stats.uniqueVisits, testid: "stat-unique-visits" },
    { label: "Subscribers", value: stats.subscribers, testid: "stat-subscribers", accent: stats.subscribers > 0 },
  ];

  return (
    <AdminLayout title="Overview">
      <p className="text-[#5C6670] font-light max-w-2xl">A quiet view of the studio. Numbers, not noise.</p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-10">
        {cards.map((c) => <StatsCard key={c.label} {...c} />)}
      </div>

      {/* Approx locations */}
      <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="admin-locations">
        <div className="bg-white border border-[#E5E5E5]">
          <div className="px-6 pt-5 pb-3 border-b border-[#E5E5E5]">
            <div className="gv-label">Where readers arrive from</div>
            <h3 className="font-serif text-2xl mt-2 text-[#1A1F24]">Top countries</h3>
          </div>
          {locations.countries.length === 0 ? (
            <p className="px-6 py-8 text-sm text-[#5C6670] italic" data-testid="no-countries">
              No location data yet — locations appear after the first visit from a real browser.
            </p>
          ) : (
            <>
              <CountryBreakdownChart countries={locations.countries} />
              <ul className="divide-y divide-[#E5E5E5] border-t border-[#E5E5E5]">
                {locations.countries.map((c, i) => (
                  <li key={c.country + i} className="px-6 py-3 flex items-center justify-between" data-testid={`country-row-${i}`}>
                    <span className="text-[#1A1F24]">
                      <span className="text-[#5C6670] tabular-nums mr-3">{String(i + 1).padStart(2, "0")}</span>
                      {c.country}
                      {c.country_code ? (
                        <span className="ml-2 text-xs tracking-[0.2em] uppercase text-[#B8BCC2]">{c.country_code}</span>
                      ) : null}
                    </span>
                    <span className="font-serif text-lg text-[#C5A059] tabular-nums">{c.count}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="bg-white border border-[#E5E5E5]">
          <div className="px-6 pt-5 pb-3 border-b border-[#E5E5E5]">
            <div className="gv-label">Cities & recent arrivals</div>
            <h3 className="font-serif text-2xl mt-2 text-[#1A1F24]">Live pulse</h3>
          </div>
          {locations.cities.length > 0 && (
            <div className="px-6 pt-4">
              <div className="text-[0.65rem] tracking-[0.28em] uppercase text-[#5C6670] mb-2">Top cities</div>
              <div className="flex flex-wrap gap-2">
                {locations.cities.map((c, i) => (
                  <span
                    key={c.city + i}
                    data-testid={`city-chip-${i}`}
                    className="text-xs px-3 py-1 border border-[#E5E5E5] text-[#1A1F24]"
                  >
                    {c.city}
                    {c.country ? <span className="text-[#B8BCC2]">, {c.country}</span> : null}
                    <span className="ml-2 text-[#C5A059] tabular-nums">{c.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="px-6 pt-5 pb-5">
            <RecentVisitsList totalVisits={stats.visits} uniqueVisits={stats.uniqueVisits} />
          </div>
        </div>
      </div>

      {/* Daily visit history — since HISTORY_START */}
      <DailyHistoryChart history={history} />

      {/* Journal read-time analytics */}
      <div className="mt-16" data-testid="admin-read-times">
        <div className="gv-label">How long readers stay</div>
        <h2 className="font-serif text-3xl md:text-4xl mt-3 text-[#1A1F24]">Journal read time</h2>
        <p className="text-sm text-[#5C6670] mt-2 max-w-xl">
          Total time spent, average per read, and unique readers per entry — captured on tab hide, route change, and page close.
        </p>
        <div className="mt-6 bg-white border border-[#E5E5E5] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[0.65rem] tracking-[0.28em] uppercase text-[#5C6670]">
              <tr className="border-b border-[#E5E5E5]">
                <th className="text-left px-4 py-3 font-normal">Entry</th>
                <th className="text-right px-4 py-3 font-normal">Reads</th>
                <th className="text-right px-4 py-3 font-normal">Likes</th>
                <th className="text-right px-4 py-3 font-normal">Unique</th>
                <th className="text-right px-4 py-3 font-normal">Avg time</th>
                <th className="text-right px-4 py-3 font-normal">Total time</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {readStats.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-[#5C6670] italic" data-testid="no-read-stats">
                    No read-time data yet — figures appear once readers spend a few seconds on any journal entry.
                  </td>
                </tr>
              )}
              {readStats.map((r) => (
                <tr key={r.id} className="border-b border-[#F0F0F0]" data-testid={`read-row-${r.slug}`}>
                  <td className="px-4 py-3 max-w-xs truncate text-[#1A1F24]">{r.title}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.views.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-[#C5A059] tabular-nums" data-testid={`read-row-likes-${r.slug}`}>
                    {(r.likes || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.unique_readers.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-[#C5A059] tabular-nums">{formatDuration(r.avg_read_seconds)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatDuration(r.total_read_seconds)}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/journal/${r.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs tracking-[0.2em] uppercase text-[#C5A059] gv-link"
                      data-testid={`read-row-open-${r.slug}`}
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {topPosts.length > 0 && topPosts[0].views > 0 && (
        <div className="mt-16" data-testid="admin-top-posts">
          <div className="gv-label">Most-read journal entries</div>
          <h2 className="font-serif text-3xl md:text-4xl mt-3 text-[#1A1F24]">Resonating with readers</h2>
          <div className="mt-8 divide-y divide-[#E5E5E5] bg-white border border-[#E5E5E5]">
            {topPosts.map((p, i) => (
              <div key={p.id} className="px-6 py-5 flex items-center gap-5" data-testid={`top-post-${i + 1}`}>
                <div className="font-serif text-3xl text-[#C5A059] w-10 flex-shrink-0">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[#1A1F24] truncate">{p.title}</div>
                  <div className="text-xs text-[#5C6670] mt-1">
                    {p.tag || "Essay"} · {new Date(p.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-serif text-2xl text-[#1A1F24]">{(p.views || 0).toLocaleString()}</div>
                  <div className="text-[0.65rem] tracking-[0.28em] uppercase text-[#5C6670]">{p.views === 1 ? "read" : "reads"}</div>
                </div>
                <Link
                  to={`/journal/${p.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs tracking-[0.2em] uppercase text-[#C5A059] gv-link ml-4 flex-shrink-0"
                  data-testid={`top-post-view-${i + 1}`}
                >
                  View →
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
